import type { ReactNode } from 'react';
import { Icon, type IconName } from './icon';
import { useAuth } from '../state/auth';
import { Link, usePath } from '../router';
import { canReviewFeedback } from '../role-access';

const primary = [
  { to: '/', label: 'All', icon: 'desk' },
  { to: '/my', label: 'My', icon: 'bell' },
  { to: '/search', label: 'Search', icon: 'search' },
  { to: '/dashboard', label: 'Dashboard', icon: 'pulse' },
  { to: '/topics', label: 'Topics', icon: 'topic' },
  { to: '/nearby', label: 'Nearby', icon: 'place' },
  { to: '/bookmarks', label: 'Saved', icon: 'bookmark' },
] satisfies Array<{ to: string; label: string; icon: IconName }>;

const secondary = [
  { to: '/places', label: 'Watch zones', icon: 'map' },
  { to: '/notifications', label: 'Notifications', icon: 'bell' },
  { to: '/settings', label: 'Settings', icon: 'settings' },
] satisfies Array<{ to: string; label: string; icon: IconName }>;

function NavigationItem({ to, label, icon }: { to: string; label: string; icon: IconName }) {
  const path = usePath();
  const active = to === '/' ? path === '/' : path === to || path.startsWith(`${to}/`);
  return <Link to={to} className={`nav-link${active ? ' nav-link--active' : ''}`}><Icon name={icon} /><span>{label}</span></Link>;
}

export function Shell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const path = usePath();
  return (
    <div className="shell">
      <a className="skip-link" href="#main-content">Skip to Main Content</a>
      <aside className="sidebar">
        <Link to="/" className="brand" aria-label="Incognito desk">
          <span className="brand__mark"><i /><i /></span>
          <span><strong>Incognito</strong><small>Intelligence desk</small></span>
        </Link>
        <p className="nav-caption">Workspace</p>
        <nav aria-label="Primary navigation" className="nav-group nav-group--primary">
          {primary.map((item) => <NavigationItem key={item.to} {...item} />)}
        </nav>
        <p className="nav-caption nav-caption--account">Account</p>
        <nav aria-label="Account navigation" className="nav-group nav-group--secondary">
          {secondary.map((item) => <NavigationItem key={item.to} {...item} />)}
          {user && canReviewFeedback(user)
            ? <><NavigationItem to="/admin/feedback" label="Review reports" icon="pulse" /><NavigationItem to="/admin" label="Administration" icon="admin" /></>
            : <NavigationItem to="/feedback" label="Feedback" icon="pulse" />}
        </nav>
        <div className="sidebar__user">
          <div className="avatar">{user?.displayName.slice(0, 2).toUpperCase()}</div>
          <div><strong>{user?.displayName}</strong><small>{user?.userGroup === 'SUPERUSER' ? 'SUPERUSER' : user?.role}</small></div>
          <button className="icon-button" aria-label="Sign out" onClick={() => void logout()}><Icon name="logout" /></button>
        </div>
      </aside>
      <div className="workspace">
        <header className="signal-bar"><div><span className="signal-bar__pulse" /> Live monitoring</div><span>Private workspace</span></header>
        <span className="sr-only">Current route: {path}</span><main className="main" id="main-content">{children}</main>
      </div>
    </div>
  );
}
